import { useCallback, useEffect, useRef, useState } from 'react'
import { logError } from '@/utils/errorUtils'

/**
 * Shared client behavior for AI-generated patient reports served through the
 * fast-read / opt-in-verify / fire-and-forget-rebuild endpoint pair (the Clinical Summary
 * Overview pattern — see its middleware handler; Risks & Gaps rides the same lifecycle).
 * The load sequence: paint the stored report fast (badge: Verifying), verify staleness,
 * auto-kick a rebuild when stale, poll while a build is in flight, verify once it lands.
 *
 * Report-type specifics (the two endpoint calls, log labels) come in through
 * `AiReportOps`; instantiating hooks (usePatientClinicalSummaryOverview,
 * usePatientRisksGaps) supply a memoized ops object.
 */

/** The refresh endpoint's state block (also embedded in every GET response). */
export interface AiReportState {
  exists: boolean
  upToDate?: boolean
  beingSuperseded: boolean
  beingSupersededSince?: string
  beingSupersededThinking?: string
  /**
   * The last rebuild FAILED (durable server-side marker). While present the server
   * also declines non-forced refreshes; only `requestRefresh` (force) retries.
   */
  lastBuildFailed?: { at: string; reason: string }
}

export interface AiReportOps<TData extends AiReportState> {
  /** One GET. checkUpToDate=false returns fast; true is the slow staleness verify. */
  read: (patientId: string, checkUpToDate: boolean) => Promise<TData>
  /** POST refresh. force = user-initiated rebuild (server still declines mid-build). */
  refresh: (patientId: string, force: boolean) => Promise<AiReportState>
  /** Human label for log messages ('clinical summary overview', 'risks & gaps report'). */
  label: string
}

// How often the report is re-read while a rebuild is in flight (the GET reports
// `beingSuperseded` until the replacement lands). Polls skip the staleness check, so
// each one is a cheap DocumentReference read.
const POLL_INTERVAL_MS = 2_000

export function usePatientAiReport<TData extends AiReportState>(
  patientId: string | undefined,
  ops: AiReportOps<TData>,
) {
  const [data, setData] = useState<TData | null>(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // One automatic rebuild kick per patient view — a stale verify after the build lands
  // must not re-kick, or mid-build data changes would loop builds.
  const autoRefreshedRef = useRef(false)
  // Whether polling observed an in-flight build, so the flag flipping false triggers
  // exactly one verifying re-read.
  const wasSupersededRef = useRef(false)
  // Monotonic id of the newest read. A read only writes state if it is still the newest
  // when it resolves, so a slow response — including one issued for a previously viewed
  // patient — can't clobber what a newer read painted.
  const readSeqRef = useRef(0)

  // One GET. Skipping the staleness check returns fast but leaves `upToDate` undefined;
  // checking is the slow, opt-in call. Returns null when the read lost the race to a
  // newer one, so callers sequencing on the result stop instead of acting on a
  // superseded snapshot.
  const read = useCallback(
    async (checkUpToDate: boolean): Promise<TData | null> => {
      const seq = ++readSeqRef.current
      if (!patientId) {
        setData(null)
        return null
      }
      setError(null)
      try {
        const result = await ops.read(patientId, checkUpToDate)
        if (seq !== readSeqRef.current) return null
        setData(result)
        return result
      } catch (err) {
        logError('patient', `Failed to load ${ops.label}`, err)
        // Keep the last painted report — a failure here is often a transient error on
        // the slow verify, and wiping content the user is already reading is worse than
        // showing it alongside the error banner.
        if (seq === readSeqRef.current) {
          setError(`Failed to load ${ops.label}.`)
        }
        return null
      }
    },
    [ops, patientId],
  )

  // The load sequence used by mount and Retry.
  const load = useCallback(
    async (isCancelled?: () => boolean) => {
      setLoading(true)
      try {
        const first = await read(false)
        if (!first || isCancelled?.()) return
        // A build is already in flight — the polling effect takes over and verifies once
        // the replacement lands.
        if (first.beingSuperseded) return

        const verified = await read(true)
        if (!verified || isCancelled?.()) return

        // Don't auto-kick when the last rebuild failed (lastBuildFailed): surface the
        // failure and let the user retry manually, rather than silently looping a build
        // that just failed. (The server also declines non-forced refreshes while the
        // marker is set — this check just avoids the pointless round-trip.)
        if (
          !verified.upToDate &&
          !verified.beingSuperseded &&
          !verified.lastBuildFailed &&
          !autoRefreshedRef.current
        ) {
          autoRefreshedRef.current = true
          try {
            // No force: the server re-checks the hashes and declines if another build
            // started (or finished) in the meantime. Its response is the authoritative
            // state either way — merging it starts the polling (beingSuperseded) or
            // settles the badge (upToDate).
            const state = await ops.refresh(patientId!, false)
            if (isCancelled?.()) return
            setData((prev) => (prev ? { ...prev, ...state } : prev))
          } catch (err) {
            logError('patient', `Failed to request ${ops.label} refresh`, err)
          }
        }
      } finally {
        setLoading(false)
      }
    },
    [ops, patientId, read],
  )

  // Ask the server to rebuild (force: the user clicked Refresh, so rebuild even when the
  // hashes say it's up to date — the server still declines while another build is in
  // flight), then re-read so `beingSuperseded` flips and polling kicks in.
  const requestRefresh = useCallback(async () => {
    if (!patientId) return
    setRefreshing(true)
    setError(null)
    try {
      await ops.refresh(patientId, true)
      await read(false)
    } catch (err) {
      logError('patient', `Failed to request ${ops.label} refresh`, err)
      setError(`Failed to request a ${ops.label} refresh.`)
    } finally {
      setRefreshing(false)
    }
  }, [ops, patientId, read])

  useEffect(() => {
    autoRefreshedRef.current = false
    wasSupersededRef.current = false
    let cancelled = false
    void load(() => cancelled)
    return () => {
      cancelled = true
      // Invalidate in-flight reads (load's, polling's, or the fire-and-forget post-build
      // verify) so a late resolution from this patient view can't write into the next
      // one or after unmount. The rule's stale-node concern doesn't apply: this is a
      // counter, and mutating the live ref is the point.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      readSeqRef.current++
    }
  }, [load])

  // While a rebuild is in flight, keep re-reading (cheaply — no staleness check) until
  // the replacement lands; then verify it once so the badge reflects an honest hash
  // compare rather than assuming the build is current.
  const beingSuperseded = data?.beingSuperseded ?? false
  useEffect(() => {
    if (beingSuperseded) {
      wasSupersededRef.current = true
      const timer = setInterval(() => {
        void read(false)
      }, POLL_INTERVAL_MS)
      return () => clearInterval(timer)
    }
    if (wasSupersededRef.current) {
      wasSupersededRef.current = false
      void read(true)
    }
    return undefined
  }, [beingSuperseded, read])

  // Event-handler-safe wrapper (load's cancellation probe must not receive a click event).
  const refresh = useCallback(async () => {
    await load()
  }, [load])

  return { data, loading, refreshing, error, refresh, requestRefresh }
}
